import { useState, useEffect, useContext } from 'react'
import { useHistory } from 'react-router-dom'
import { has, isArray } from 'lodash'

import { toast } from 'react-toastify'

import { i18n } from '../../translate/i18n'
import api from '../../services/api'
import toastError from '../../errors/toastError'
import { SocketContext } from '../../context/Socket/SocketContext'
import moment from 'moment'

const REFRESH_TOKEN_URL = '/auth/refresh_token'

let refreshTokenPromise = null

// Compartilha uma única renovação entre todas as requisições e instâncias do hook.
const refreshToken = () => {
  if (!refreshTokenPromise) {
    refreshTokenPromise = api.post(REFRESH_TOKEN_URL).finally(() => {
      refreshTokenPromise = null
    })
  }
  return refreshTokenPromise
}

api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers['Authorization'] = `Bearer ${JSON.parse(token)}`
    }
    return config
  },
  error => Promise.reject(error)
)

const useAuth = () => {
  const history = useHistory()
  const [isAuth, setIsAuth] = useState(false)
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState(() => {
    const companyExpired = JSON.parse(localStorage.getItem('companyExpired') || 'false')
    return { companyExpired }
  })

  useEffect(() => {
    const responseInterceptor = api.interceptors.response.use(
      response => {
        return response
      },
      async error => {
        const originalRequest = error.config
        if (error?.response?.status === 403 && originalRequest && !originalRequest._retry && originalRequest.url !== REFRESH_TOKEN_URL) {
          originalRequest._retry = true

          const { data } = await refreshToken()
          if (data) {
            localStorage.setItem('token', JSON.stringify(data.token))
            api.defaults.headers.Authorization = `Bearer ${data.token}`

            const companyExpired = data.companyExpired ?? JSON.parse(localStorage.getItem('companyExpired') || 'false')
            localStorage.setItem('companyExpired', JSON.stringify(companyExpired))

            if (data.user) {
              setUser(prev => ({ ...prev, ...data.user, companyExpired }))
            }
          }
          return api(originalRequest)
        }
        if (error?.response?.status === 401) {
          localStorage.removeItem('token')
          localStorage.removeItem('companyId')
          localStorage.removeItem('companyExpired')
          api.defaults.headers.Authorization = undefined
          setIsAuth(false)
          if (error?.config?.method === 'get' && error?.config?.url?.startsWith('/whatsapp')) {
            history.push('/login')
          }
        }
        return Promise.reject(error)
      }
    )

    return () => {
      api.interceptors.response.eject(responseInterceptor)
    }
  }, [history])

  const socketManager = useContext(SocketContext)

  useEffect(() => {
    const token = localStorage.getItem('token')
    ;(async () => {
      if (token) {
        try {
          const { data } = await refreshToken()
          localStorage.setItem('token', JSON.stringify(data.token))
          api.defaults.headers.Authorization = `Bearer ${data.token}`
          setIsAuth(true)
          const companyExpired = data.companyExpired ?? JSON.parse(localStorage.getItem('companyExpired') || 'false')
          localStorage.setItem('companyExpired', JSON.stringify(companyExpired))
          setUser({ ...data.user, companyExpired })
          if (companyExpired) {
            history.push('/financeiro')
          }
        } catch (err) {
          toastError(err)
        }
      }
      setLoading(false)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const companyId = localStorage.getItem('companyId')
    if (companyId) {
      const socket = socketManager.getSocket(companyId)

      socket.on(`company-${companyId}-user`, data => {
        if (data.action === 'update' && data.user.id === user.id) {
          const companyExpired = data.user.companyExpired ?? JSON.parse(localStorage.getItem('companyExpired') || 'false')
          localStorage.setItem('companyExpired', JSON.stringify(companyExpired))
          setUser({ ...data.user, companyExpired })
        }
      })

      return () => {
        socket.disconnect()
      }
    }
  }, [socketManager, user])

  const handleLogin = async userData => {
    setLoading(true)

    try {
      const { data } = await api.post('/auth/login', userData)
      const {
        user: { companyId, id, company },
      } = data

      if (has(company, 'settings') && isArray(company.settings)) {
        const setting = company.settings.find(s => s.key === 'campaignsEnabled')
        if (setting && setting.value === 'true') {
          localStorage.setItem('cshow', null) //regra pra exibir campanhas
        }
      }

      moment.locale('pt-br')
      const dueDate = data.user.company.dueDate
      // TODO non used
      // const hoje = moment(moment()).format('DD/MM/yyyy')
      const vencimento = moment(dueDate).format('DD/MM/yyyy')

      const diff = moment(dueDate).diff(moment())
      const dias = moment.duration(diff).asDays()

      localStorage.setItem('token', JSON.stringify(data.token))
      localStorage.setItem('companyId', companyId)
      localStorage.setItem('userId', id)
      localStorage.setItem('companyDueDate', vencimento)
      localStorage.setItem('companyExpired', JSON.stringify(data.user.companyExpired))
      api.defaults.headers.Authorization = `Bearer ${data.token}`
      setUser(data.user)
      setIsAuth(true)
      toast.success(i18n.t('auth.toasts.success'))
      if (Math.round(dias) < 5 && dias >= 0) {
        toast.warn(`Sua assinatura vence em ${Math.round(dias)} ${Math.round(dias) === 1 ? 'dia' : 'dias'} `)
      }
      if (data.user.companyExpired) {
        history.push('/financeiro')
      } else {
        history.push('/tickets')
      }
      setLoading(false)

      //quebra linha
    } catch (err) {
      toastError(err)
      setLoading(false)
    }
  }

  const handleLogout = async () => {
    setLoading(true)

    try {
      await api.delete('/auth/logout')
      setIsAuth(false)
      setUser({})
      localStorage.removeItem('token')
      localStorage.removeItem('companyId')
      localStorage.removeItem('userId')
      localStorage.removeItem('companyExpired')
      localStorage.removeItem('cshow')
      localStorage.removeItem('genericErrorCount')
      api.defaults.headers.Authorization = undefined
      setLoading(false)
      history.push('/login')
    } catch (err) {
      toastError(err)
      setLoading(false)
    }
  }

  // Troca a sessão (empresa/usuário) e recarrega a aplicação para reiniciar sockets e contextos.
  const switchSession = async url => {
    try {
      const { data } = await api.post(url)
      const { companyId, id, company, companyExpired } = data.user

      localStorage.setItem('token', JSON.stringify(data.token))
      localStorage.setItem('companyId', companyId)
      localStorage.setItem('userId', id)
      localStorage.setItem('companyDueDate', moment(company?.dueDate).format('DD/MM/yyyy'))
      localStorage.setItem('companyExpired', JSON.stringify(companyExpired))
      localStorage.removeItem('cshow')
      api.defaults.headers.Authorization = `Bearer ${data.token}`

      window.location.href = companyExpired ? '/financeiro' : '/tickets'
    } catch (err) {
      toastError(err)
    }
  }

  const handleImpersonate = companyId => switchSession(`/auth/impersonate/${companyId}`)

  const handleStopImpersonation = () => switchSession('/auth/impersonate/stop')

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get('/auth/me')
      return data
    } catch (err) {
      toastError(err)
    }
  }

  return {
    isAuth,
    user,
    loading,
    handleLogin,
    handleLogout,
    handleImpersonate,
    handleStopImpersonation,
    getCurrentUserInfo,
  }
}

export default useAuth
